/**
 * Version Service
 * Handles automatic app updates and refresh notifications
 */

import versionServiceDetectionMixin from './versionService/versionServiceDetection';
import versionServiceLifecycleMixin from './versionService/versionServiceLifecycle';

class VersionService {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.isChecking = false;
    this.listeners = [];
    this.lastCheckTime = 0;
    this.checkIntervalMs = 30000; // Check every 30 seconds (was 5 minutes)
    this.minCheckInterval = 10000; // Minimum 10 seconds between checks (was 1 minute)
    this.focusCheckDelay = 2000; // Check 2 seconds after page focus
    this.initialized = false;
    this.visibilityHandler = null;
    this.onlineHandler = null;
    this.focusHandler = null;
    this.quizFlowGracePeriodMs = 15000;
  }

  /**
   * Refresh the application
   */
  refresh() {
    console.log('Refreshing application...');
    window.location.reload();
  }

  /**
   * Force clear all caches and refresh the app
   */
  async forceRefresh() {
    console.log('Force refreshing app...');

    try {
      // Clear service worker cache
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.active) {
          // Send message to service worker to clear cache
          registration.active.postMessage({ type: 'CLEAR_CACHE' });

          // Wait a bit for cache to clear, then force reload
          setTimeout(() => {
            window.location.reload(true);
          }, 1000);
        } else {
          // No service worker, just reload
          window.location.reload(true);
        }
      } else {
        // No service worker support, just reload
        window.location.reload(true);
      }
    } catch (error) {
      console.error('Error during force refresh:', error);
      // Fallback: just reload
      window.location.reload(true);
    }
  }

  /**
   * Clear browser cache using various methods
   */
  async clearBrowserCache() {
    console.log('Clearing browser cache...');

    try {
      // Clear service worker cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Service worker caches cleared');
      }

      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      console.log('Local storage cleared');

      // Clear IndexedDB if it exists
      if ('indexedDB' in window) {
        // Note: This is a simplified approach. In production, you might want to be more specific
        console.log('IndexedDB exists but not cleared (requires specific implementation)');
      }

    } catch (error) {
      console.error('Error clearing browser cache:', error);
    }
  }

  /**
   * Get the build ID for cache busting
   */
  getBuildId() {
    return this.versionData?.buildHash || this.currentVersion || 'unknown';
  }

  /**
   * Get full version info for display
   */
  getVersionInfo() {
    return {
      version: this.versionData?.version || '1.0.0',
      buildHash: this.getBuildId(),
      buildTime: this.versionData?.buildTime,
      environment: this.versionData?.environment || process.env.NODE_ENV || 'development'
    };
  }

  /**
   * Get version data for debugging
   */
  getVersionData() {
    return this.versionData || null;
  }

}

Object.assign(VersionService.prototype, versionServiceDetectionMixin);
Object.assign(VersionService.prototype, versionServiceLifecycleMixin);

// Create singleton instance
const versionService = new VersionService();

export default versionService;
